import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

/**
 * Realistic VerOps dashboard mock for the hero — mirrors the actual app: the
 * blue→green "V" logo, the real grouped left-nav, KPI tiles, a latency line
 * chart, a throughput bar chart, a distributed-trace waterfall and an alerts
 * feed. GitHub-dark surfaces (#0d1117 / #161b22), blue primary — matching the
 * product theme. Charts draw themselves in (reuses the .vo-* keyframes defined
 * by AuroraBackground; degrade gracefully without them).
 */
const BLUE = '#3b82f6';
const GREEN = '#22c55e';
const INK = '#e6edf3';
const MUTED = '#8b949e';
const SURFACE = '#0d1117';
const PANEL = '#161b22';
const BORDER = 'rgba(240,246,252,0.08)';
const LOGO_SRC = `${process.env.PUBLIC_URL}/logo512.png`;

const NAV = [
  { label: 'Overview', items: [
    { t: 'Home', icon: <HomeRoundedIcon sx={{ fontSize: 15 }} /> },
    { t: 'Dashboards', icon: <DashboardRoundedIcon sx={{ fontSize: 15 }} />, active: true } ] },
  { label: 'Monitor', items: [
    { t: 'Applications', icon: <CodeRoundedIcon sx={{ fontSize: 15 }} /> },
    { t: 'Network', icon: <HubRoundedIcon sx={{ fontSize: 15 }} /> },
    { t: 'Alerts', icon: <NotificationsRoundedIcon sx={{ fontSize: 15 }} /> } ] },
  { label: 'Analyze', items: [
    { t: 'Synthetics', icon: <SmartToyRoundedIcon sx={{ fontSize: 15 }} /> },
    { t: 'Workbooks', icon: <AccountTreeRoundedIcon sx={{ fontSize: 15 }} /> } ] },
  { label: 'AI', items: [
    { t: 'Sentinel', icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 15 }} /> } ] },
];

const Kpi = ({ k, v, c, sub }) => (
  <Box sx={{ flex: 1, minWidth: 0, p: 1.25, borderRadius: 1.5, background: PANEL, border: `1px solid ${BORDER}` }}>
    <Typography sx={{ color: MUTED, fontSize: 10.5, mb: 0.25, whiteSpace: 'nowrap' }}>{k}</Typography>
    <Typography sx={{ color: c || INK, fontSize: 18, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
    {sub && <Typography sx={{ color: GREEN, fontSize: 9.5, fontWeight: 600 }}>{sub}</Typography>}
  </Box>
);

const Panel = ({ title, badge, children, sx }) => (
  <Box sx={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.25, ...sx }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
      <Typography sx={{ color: INK, fontSize: 11.5, fontWeight: 700 }}>{title}</Typography>
      {badge && <Typography sx={{ color: MUTED, fontSize: 9.5 }}>{badge}</Typography>}
    </Stack>
    {children}
  </Box>
);

const ProductPreview = () => (
  <Box sx={{ borderRadius: 2.5, overflow: 'hidden', border: `1px solid ${BORDER}`, background: SURFACE,
    boxShadow: '0 44px 110px rgba(0,0,0,0.6)', maxWidth: 1000, mx: 'auto', textAlign: 'left' }}>
    {/* browser chrome */}
    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1, background: '#0a0d12', borderBottom: `1px solid ${BORDER}` }}>
      {['#ef4444', '#f59e0b', '#22c55e'].map((c) => (
        <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.85 }} />
      ))}
      <Box sx={{ ml: 1, px: 1.5, py: 0.4, borderRadius: 1, background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${BORDER}`, color: MUTED, fontSize: 11 }}>app.verops.io/dashboards</Box>
    </Stack>

    <Stack direction="row" sx={{ minHeight: { xs: 360, md: 430 } }}>
      {/* sidebar */}
      <Box sx={{ width: 168, flexShrink: 0, background: '#0b0f15', borderRight: `1px solid ${BORDER}`,
        p: 1.25, display: { xs: 'none', sm: 'block' } }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5, px: 0.5 }}>
          <Box component="img" src={LOGO_SRC} alt="" sx={{ width: 20, height: 20 }} />
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography component="span" sx={{ fontWeight: 800, fontSize: 13, color: BLUE }}>V</Typography>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: 12.5, color: INK }}>erOps</Typography>
          </Box>
        </Stack>
        {NAV.map((grp) => (
          <Box key={grp.label} sx={{ mb: 1 }}>
            <Typography sx={{ color: '#586172', fontSize: 8.5, fontWeight: 700, letterSpacing: 1, px: 0.75, mb: 0.4 }}>
              {grp.label.toUpperCase()}
            </Typography>
            {grp.items.map((it) => (
              <Stack key={it.t} direction="row" spacing={0.75} alignItems="center"
                sx={{ px: 0.75, py: 0.5, borderRadius: 1, mb: 0.2,
                  background: it.active ? 'rgba(59,130,246,0.16)' : 'transparent',
                  color: it.active ? '#bcd2ff' : MUTED,
                  borderLeft: it.active ? `2px solid ${BLUE}` : '2px solid transparent' }}>
                {it.icon}
                <Typography sx={{ fontSize: 11, fontWeight: it.active ? 700 : 500 }}>{it.t}</Typography>
              </Stack>
            ))}
          </Box>
        ))}
      </Box>

      {/* main */}
      <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ color: INK, fontSize: 14, fontWeight: 800 }}>Production · Overview</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ px: 1, py: 0.3, borderRadius: 1, background: PANEL, border: `1px solid ${BORDER}`, color: MUTED, fontSize: 10 }}>Last 24h</Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1, py: 0.3, borderRadius: 1,
              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: GREEN }} className="vo-pulse" />
              <Typography sx={{ color: '#86efac', fontSize: 10, fontWeight: 700 }}>Live</Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* KPIs */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
          <Kpi k="Uptime (SLO)" v="99.98%" c={GREEN} sub="▲ on target" />
          <Kpi k="p95 latency" v="412ms" sub="▼ 8%" />
          <Kpi k="Throughput" v="18.4k/s" />
          <Kpi k="AI faithfulness" v="0.94" c={GREEN} />
          <Kpi k="Open incidents" v="0" c={GREEN} />
        </Stack>

        {/* charts row */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
          <Panel title="Request latency" badge="p50 · p95 · p99" sx={{ flex: 2 }}>
            <Box sx={{ height: 96 }}>
              <svg viewBox="0 0 360 100" width="100%" height="100%" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="pp-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity="0.30" /><stop offset="100%" stopColor={BLUE} stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[25, 50, 75].map((y) => <line key={y} x1="0" x2="360" y1={y} y2={y} stroke="#ffffff" strokeOpacity="0.05" />)}
                <path d="M0 78 L40 70 L80 74 L120 54 L160 60 L200 40 L240 50 L280 30 L320 44 L360 26 L360 100 L0 100 Z" fill="url(#pp-area)" />
                <path className="vo-draw" d="M0 78 L40 70 L80 74 L120 54 L160 60 L200 40 L240 50 L280 30 L320 44 L360 26"
                  fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <path className="vo-draw" style={{ animationDelay: '0.4s' }}
                  d="M0 88 L40 84 L80 86 L120 76 L160 80 L200 70 L240 74 L280 66 L320 72 L360 62"
                  fill="none" stroke={GREEN} strokeWidth="2" strokeOpacity="0.85" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Box>
          </Panel>
          <Panel title="Throughput" badge="req/s" sx={{ flex: 1 }}>
            <Box sx={{ height: 96 }}>
              <svg viewBox="0 0 180 100" width="100%" height="100%" preserveAspectRatio="none">
                <line x1="0" x2="180" y1="92" y2="92" stroke="#ffffff" strokeOpacity="0.1" />
                {[40, 58, 34, 72, 50, 84, 62, 76, 46, 68].map((h, i) => (
                  <rect key={i} className="vo-bar" x={6 + i * 17} y={92 - h} width="11" height={h} rx="2"
                    fill={i % 3 === 0 ? GREEN : BLUE} fillOpacity="0.8"
                    style={{ transformOrigin: '0 92px', animationDelay: `${i * 0.1}s` }} />
                ))}
              </svg>
            </Box>
          </Panel>
        </Stack>

        {/* trace + alerts */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <Panel title="Distributed trace" badge="checkout · 512ms" sx={{ flex: 2 }}>
            {[
              { t: 'api-gateway', x: 0, w: 100, c: BLUE },
              { t: 'auth-svc', x: 8, w: 32, c: GREEN },
              { t: 'cart-svc', x: 24, w: 58, c: BLUE },
              { t: 'postgres', x: 40, w: 30, c: '#a78bfa' },
              { t: 'payment-api', x: 64, w: 30, c: GREEN },
            ].map((s, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.6 }}>
                <Typography sx={{ width: 64, flexShrink: 0, color: MUTED, fontSize: 9.5, textAlign: 'right',
                  fontFamily: 'monospace' }}>{s.t}</Typography>
                <Box sx={{ flex: 1, height: 9, background: 'rgba(255,255,255,0.04)', borderRadius: 4, position: 'relative' }}>
                  <Box className="vo-span" sx={{ position: 'absolute', left: `${s.x}%`, width: `${s.w}%`, height: '100%',
                    borderRadius: 4, background: s.c, opacity: 0.88, animationDelay: `${i * 0.12}s` }} />
                </Box>
              </Stack>
            ))}
          </Panel>
          <Panel title="Alerts" badge="2 active" sx={{ flex: 1 }}>
            {[
              { sev: '#f59e0b', t: 'p95 latency high', s: 'cart-svc' },
              { sev: '#3b82f6', t: 'Deploy gate passed', s: 'CI · web' },
              { sev: '#22c55e', t: 'SLO burn normal', s: 'checkout' },
            ].map((a, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.65 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: a.sev, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: INK, fontSize: 10.5, fontWeight: 600, lineHeight: 1.2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.t}</Typography>
                  <Typography sx={{ color: MUTED, fontSize: 9 }}>{a.s}</Typography>
                </Box>
              </Stack>
            ))}
          </Panel>
        </Stack>
      </Box>
    </Stack>
  </Box>
);

export default ProductPreview;
